
'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getAuth, onAuthStateChanged, GoogleAuthProvider, signInWithPopup, signOut, User } from 'firebase/auth';
import { initializeApp, getApps, getApp, FirebaseApp } from 'firebase/app';
import { firebaseConfig } from '@/lib/firebase/client';
import Loading from '@/app/loading';
import { isAdminEmail } from '@/lib/admin/admin-emails';

// Define the shape of the context state
interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAdmin: boolean;
  signInWithGoogle: () => Promise<void>;
  signOutUser: () => Promise<void>;
}

// Create the context
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Create the provider component
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Ensure Firebase is initialized
  const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];
  const auth = getAuth(app);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Mint (or refresh) the HttpOnly session cookie so that browser-native
        // requests (e.g. Next.js image optimisation fetching resource images)
        // are authenticated without needing a JavaScript-injected header.
        try {
          // forceRefresh: true ensures the token's iat is < 5 minutes old,
          // which Firebase requires for createSessionCookie.
          const idToken = await user.getIdToken(/* forceRefresh */ true);
          await fetch('/api/auth/session', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ idToken }),
          });
        } catch (error) {
          console.error('Failed to create session cookie:', error);
        }
      }

      setUser(user);
      setIsAdmin(user ? isAdminEmail(user.email) : false);
      setIsLoading(false);
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [auth]);

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
      // Session cookie is created by the onAuthStateChanged handler above.
    } catch (error) {
      console.error("Error signing in with Google: ", error);
    }
  };

  const signOutUser = async () => {
    try {
      // Clear the session cookie before Firebase sign-out so the server
      // can no longer authenticate browser-native requests.
      await fetch('/api/auth/session', { method: 'DELETE' });
      await signOut(auth);
    } catch (error) {
      console.error("Error signing out: ", error);
    }
  };

  const value = {
    user,
    isLoading,
    isAdmin,
    signInWithGoogle,
    signOutUser,
  };

  // Render a loading state while checking for authentication status
  if (isLoading) {
    return <Loading />;
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Create a custom hook to use the auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
